// src/pages/ELearning/TestsList.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTests, softDeleteTest, toggleFavorite, getFavoriteTests, getCompanyUsers, createAssignment, getAllTags, bulkAssign, getAssignmentFilters, exportTestsExcel } from '../../api/client';
import {
  Search, Clock, Award, BookOpen, Loader2, AlertCircle, Plus, Archive, Edit, Trash2, Star, Heart, Send, X, Check, QrCode, Copy, Download, Mail, BadgeCheck, ArrowUpDown, ClipboardList, UserPlus
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import api from '../../api/client';  // ← НОВОЕ: импорт api для отправки теста

interface Test {
  _id: string;
  title: string;
  description: string;
  image?: string;
  category: string;
  timeLimit: number;
  points: number;
  questionsCount?: number;
  rating?: number;
  tags?: string[];
}

// ==================== НОВОЕ: Модальное окно отправки теста ====================
const SendTestModal: React.FC<{
  isOpen: boolean;
  test: Test | null;
  onClose: () => void;
}> = ({ isOpen, test, onClose }) => {
  const [tab, setTab] = useState<'email' | 'qr'>('email');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [qrData, setQrData] = useState<{ link: string; testTitle: string; token: string } | null>(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const { showToast } = useToast();

  if (!isOpen || !test) return null;

  const handleSend = async () => {
    if (!email.trim()) {
      setError('Введите email');
      return;
    }
    setSending(true);
    setError('');
    try {
      await api.post('/tests/send', { testId: test._id, email });
      setSent(true);
      showToast('Тест отправлен!', 'success');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Не удалось отправить тест');
    } finally {
      setSending(false);
    }
  };

  const generateQr = async () => {
    setLoadingQr(true);
    setError('');
    try {
      const r = await api.post('/tests/create-link', { testId: test._id });
      setQrData({
        link: r.data.link,
        testTitle: r.data.testTitle,
        token: r.data.token,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Не удалось создать ссылку');
    } finally {
      setLoadingQr(false);
    }
  };

  const handleSwitchToQr = () => {
    setTab('qr');
    if (!qrData) generateQr();
  };

  const handleCopyLink = async () => {
    if (!qrData?.link) return;
    try {
      await navigator.clipboard.writeText(qrData.link);
      showToast('Ссылка скопирована', 'success');
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = qrData.link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showToast('Ссылка скопирована', 'success');
    }
  };

  const handleDownloadPng = () => {
    const svg = document.getElementById('qr-svg-element') as unknown as SVGElement | null;
    if (!svg) return;
    try {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const size = 1024;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        canvas.toBlob(blob => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'qr-test-' + test._id + '.png';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 'image/png');
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (e) {
      console.error('PNG export error:', e);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSent(false);
    setError('');
    setTab('email');
    setQrData(null);
    onClose();
  };

  return (
    <div style={modalStyles.overlay} onClick={handleClose}>
      <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={modalStyles.title}>Отправить тест</h3>
          <button onClick={handleClose} style={modalStyles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <p style={modalStyles.testName}>{test.title}</p>

        {/* ─── Табы ─── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <button
            onClick={() => setTab('email')}
            style={{
              flex: 1,
              padding: '10px 12px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === 'email' ? '2px solid #2575fc' : '2px solid transparent',
              color: tab === 'email' ? '#2575fc' : '#666',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <Mail size={16} /> Email
          </button>
          <button
            onClick={handleSwitchToQr}
            style={{
              flex: 1,
              padding: '10px 12px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === 'qr' ? '2px solid #2575fc' : '2px solid transparent',
              color: tab === 'qr' ? '#2575fc' : '#666',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <QrCode size={16} /> QR-код
          </button>
        </div>

        <div style={modalStyles.body}>
          {/* ─── TAB: EMAIL ─── */}
          {tab === 'email' && (
            <>
              {sent ? (
                <div style={modalStyles.success}>
                  <Check size={24} color="#2ecc71" />
                  <p>Тест отправлен на {email}</p>
                </div>
              ) : (
                <>
                  <label style={modalStyles.label}>Email получателя</label>
                  <input
                    type="email"
                    placeholder="user@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    style={modalStyles.input}
                  />
                  {error && <p style={modalStyles.error}>{error}</p>}
                </>
              )}
            </>
          )}

          {/* ─── TAB: QR ─── */}
          {tab === 'qr' && (
            <div style={{ textAlign: 'center' }}>
              {loadingQr && (
                <div style={{ padding: 40 }}>
                  <Loader2 className="spin" size={32} />
                  <p style={{ color: '#666', fontSize: 13 }}>Создание ссылки...</p>
                </div>
              )}

              {!loadingQr && qrData && (
                <>
                  <div style={{
                    display: 'inline-block',
                    padding: 16,
                    background: '#fff',
                    borderRadius: 16,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    marginBottom: 16,
                  }}>
                    <QRCodeSVG
                      id="qr-svg-element"
                      value={qrData.link}
                      size={220}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                  <p style={{ fontSize: 12, color: '#888', marginBottom: 12, lineHeight: 1.5 }}>
                    Покажите QR сотрудникам — они откроют тест<br/>
                    на телефоне без логина
                  </p>
                  <div style={{
                    fontSize: 11,
                    color: '#666',
                    wordBreak: 'break-all',
                    padding: '8px 12px',
                    background: '#f5f7fa',
                    borderRadius: 8,
                    marginBottom: 12,
                    fontFamily: 'monospace',
                  }}>
                    {qrData.link}
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button
                      onClick={handleCopyLink}
                      style={{
                        padding: '8px 16px',
                        background: '#2575fc',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 20,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <Copy size={14} /> Копировать
                    </button>
                    <button
                      onClick={handleDownloadPng}
                      style={{
                        padding: '8px 16px',
                        background: '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 20,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <Download size={14} /> PNG
                    </button>
                  </div>
                </>
              )}

              {!loadingQr && !qrData && error && (
                <p style={modalStyles.error}>{error}</p>
              )}
            </div>
          )}
        </div>

        <div style={modalStyles.footer}>
          <button onClick={handleClose} style={modalStyles.cancelButton}>
            {sent ? 'Закрыть' : 'Отмена'}
          </button>
          {tab === 'email' && !sent && (
            <button onClick={handleSend} disabled={sending} style={modalStyles.sendButton}>
              <Send size={16} /> {sending ? 'Отправка...' : 'Отправить'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
//  МОДАЛ НАЗНАЧЕНИЯ ТЕСТА (для HR)
// ═══════════════════════════════════════════════════════════
const AssignTestModal: React.FC<{
  isOpen: boolean;
  test: Test | null;
  onClose: () => void;
}> = ({ isOpen, test, onClose }) => {
  const [users, setUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deadline, setDeadline] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { showToast } = useToast();
  // ─── ФИЧА C: Табы ───
  const [assignTab, setAssignTab] = useState<'manual' | 'department' | 'role'>('manual');
  const [filters, setFilters] = useState<{ departments: string[]; roles: string[] }>({ departments: [], roles: [] });
  const [selectedFilter, setSelectedFilter] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    getAssignmentFilters()
      .then((data: any) => setFilters({
        departments: data.departments || [],
        roles: data.roles || [],
      }))
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingUsers(true);
    getCompanyUsers()
      .then((data: any) => {
        const list = Array.isArray(data) ? data : (data.items || []);
        setUsers(list);
      })
      .catch((e: any) => {
        console.error('load users error:', e);
        setError('Не удалось загрузить сотрудников');
      })
      .finally(() => setLoadingUsers(false));
  }, [isOpen]);

  if (!isOpen || !test) return null;

  const toggleUser = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleAssign = async () => {
    if (assignTab === 'manual' && selected.size === 0) {
      setError('Выберите хотя бы одного сотрудника');
      return;
    }
    if (assignTab !== 'manual' && !selectedFilter) {
      setError(assignTab === 'department' ? 'Выберите отдел' : 'Выберите роль');
      return;
    }
    if (!deadline) {
      setError('Укажите дедлайн');
      return;
    }
    setLoading(true);
    setError('');
    try {
      let r;
      if (assignTab === 'manual') {
        r = await createAssignment(
          test._id,
          Array.from(selected),
          new Date(deadline).toISOString(),
          notes || undefined,
        );
      } else {
        r = await bulkAssign({
          testId: test._id,
          deadline: new Date(deadline).toISOString(),
          notes: notes || undefined,
          filter: { type: assignTab, value: selectedFilter },
        });
      }
      setSuccess(true);
      showToast('Назначено: ' + (r.created || 0) + ' сотрудникам', 'success');
      setTimeout(() => onClose(), 1500);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Не удалось назначить тест');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelected(new Set());
    setNotes('');
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <div style={modalStyles.overlay} onClick={handleClose}>
      <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={modalStyles.title}>📋 Назначить тест</h3>
          <button onClick={handleClose} style={modalStyles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <p style={modalStyles.testName}>{test.title}</p>

        {success ? (
          <div style={modalStyles.success}>
            <Check size={24} color="#2ecc71" />
            <p>Назначено {selected.size} сотрудникам</p>
          </div>
        ) : (
          <>
            {/* ─── ФИЧА C: Табы ─── */}
        {!success && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: 'rgba(102,126,234,0.06)', borderRadius: 12, padding: 4 }}>
            {[
              { key: 'manual', label: '👥 Вручную' },
              { key: 'department', label: '🏢 По отделу' },
              { key: 'role', label: '🎭 По роли' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => { setAssignTab(t.key as any); setSelectedFilter(''); }}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: 10,
                  background: assignTab === t.key ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                  color: assignTab === t.key ? '#fff' : '#5a5f7a',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  boxShadow: assignTab === t.key ? '0 4px 12px rgba(102,126,234,0.3)' : 'none',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* ═══ TABFIX: Условный рендер по табам ═══ */}
        {assignTab === 'manual' && (
          <div style={{ marginBottom: 16 }}>
            <label style={modalStyles.label}>
              Кому назначить {selected.size > 0 ? '(' + selected.size + ' выбрано)' : ''}
            </label>
            {loadingUsers ? (
              <div style={{ padding: 20, textAlign: 'center' }}>
                <Loader2 className="spin" size={20} />
              </div>
            ) : (
              <div style={{
                maxHeight: 220,
                overflowY: 'auto',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: 12,
                background: 'rgba(255,255,255,0.7)',
              }}>
                {users.length === 0 && (
                  <p style={{ padding: 16, color: '#999', fontSize: 13, textAlign: 'center' }}>
                    Нет сотрудников
                  </p>
                )}
                {users.map(u => (
                  <label
                    key={u.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(0,0,0,0.05)',
                      background: selected.has(u.id) ? 'rgba(37,117,252,0.08)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleUser(u.id)}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name || 'Без имени'}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{u.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {assignTab === 'department' && (
          <div style={{ marginBottom: 16 }}>
            <label style={modalStyles.label}>Выберите отдел</label>
            {filters.departments.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13, background: 'rgba(0,0,0,0.03)', borderRadius: 12 }}>
                Нет отделов с сотрудниками
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                {filters.departments.map(dep => (
                  <label
                    key={dep}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '12px 14px',
                      cursor: 'pointer',
                      border: selectedFilter === dep ? '2px solid #667eea' : '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 12,
                      background: selectedFilter === dep ? 'rgba(102,126,234,0.08)' : 'rgba(255,255,255,0.7)',
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    <input
                      type="radio"
                      name="department"
                      checked={selectedFilter === dep}
                      onChange={() => setSelectedFilter(dep)}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    🏢 {dep}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {assignTab === 'role' && (
          <div style={{ marginBottom: 16 }}>
            <label style={modalStyles.label}>Выберите роль</label>
            {filters.roles.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13, background: 'rgba(0,0,0,0.03)', borderRadius: 12 }}>
                Нет ролей
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                {filters.roles.map(role => (
                  <label
                    key={role}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '12px 14px',
                      cursor: 'pointer',
                      border: selectedFilter === role ? '2px solid #667eea' : '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 12,
                      background: selectedFilter === role ? 'rgba(102,126,234,0.08)' : 'rgba(255,255,255,0.7)',
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    <input
                      type="radio"
                      name="role"
                      checked={selectedFilter === role}
                      onChange={() => setSelectedFilter(role)}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    🎭 {role}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

            <div style={{ marginBottom: 16 }}>
              <label style={modalStyles.label}>Дедлайн</label>
              <input
                type="date"
                value={deadline}
                onChange={e => setDeadline(e.target.value)}
                style={modalStyles.input}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={modalStyles.label}>Заметка (необязательно)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Например: пройди до конца месяца"
                rows={2}
                style={{ ...modalStyles.input, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            {error && <p style={modalStyles.error}>{error}</p>}
          </>
        )}

        <div style={modalStyles.footer}>
          <button onClick={handleClose} style={modalStyles.cancelButton}>
            {success ? 'Закрыть' : 'Отмена'}
          </button>
          {!success && (
            <button
              onClick={handleAssign}
              disabled={loading || selected.size === 0}
              style={{
                ...modalStyles.sendButton,
                background: selected.size === 0 ? '#95a5a6' : 'linear-gradient(135deg, #6a11cb, #2575fc)',
                cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              <ClipboardList size={16} /> {loading ? 'Назначение...' : 'Назначить'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
  },
  modal: {
    background: 'rgba(255,255,255,0.85)',
    borderRadius: 32,
    padding: 32,
    width: 440,
    maxWidth: '90%',
    boxShadow: '0 25px 45px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: 0,
  },
  closeButton: {
    background: 'rgba(255,255,255,0.5)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#4a4a68',
    transition: 'all 0.2s',
  },
  body: {
    marginBottom: 24,
  },
  testName: {
    fontSize: 16,
    fontWeight: 600,
    color: '#2575fc',
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 500,
    color: '#4a4a68',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    padding: '14px 20px',
    borderRadius: 16,
    border: '1px solid rgba(0,0,0,0.1)',
    background: 'rgba(255,255,255,0.7)',
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
  },
  error: {
    color: '#ff4d4f',
    fontSize: 13,
    marginTop: 8,
  },
  success: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    color: '#2ecc71',
    fontWeight: 500,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
  },
  cancelButton: {
    background: 'transparent',
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: 40,
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    color: '#666',
  },
  sendButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 40,
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(37,117,252,0.3)',
  },
};
// ==================== КОНЕЦ НОВОГО ====================

// ==================== Основной компонент TestsList ====================
const TestsList: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [tests, setTests] = useState<Test[]>([]);
  const { user } = useAuth();
  const canAssign = ['admin', 'superadmin', 'hr_manager'].includes(user?.role || '');
  
  // ═══ URL_FILTERS_PATCH: фильтры в URL (сохраняются при F5) ═══
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('q') || '';
  const category = searchParams.get('cat') || 'all';
  const selectedTag = searchParams.get('tag') || 'all';
  const statusFilter = (searchParams.get('status') || 'all') as 'all' | 'passed' | 'new';
  const sortBy = (searchParams.get('sort') || 'date') as 'date' | 'rating' | 'title' | 'time';

  // Хелпер: обновить один параметр URL
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all' || (key === 'sort' && value === 'date') || (key === 'q' && !value.trim())) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const setSearch = (v: string) => setParam('q', v);
  const setCategory = (v: string) => setParam('cat', v);
  const setSelectedTag = (v: string) => setParam('tag', v);
  const setStatusFilter = (v: 'all' | 'passed' | 'new') => setParam('status', v);
  const setSortBy = (v: 'date' | 'rating' | 'title' | 'time') => setParam('sort', v);

  // Сбросить все фильтры
  const resetFilters = () => setSearchParams({}, { replace: true });
  const [exportingTests, setExportingTests] = useState(false);

  // ─── ФИЧА M: Экспорт тестов в Excel ───
  const handleExportTests = async () => {
    setExportingTests(true);
    try {
      const res = await exportTestsExcel();
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'elearning-tests-' + new Date().toISOString().slice(0, 10) + '.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert('Ошибка экспорта: ' + e.message);
    } finally {
      setExportingTests(false);
    }
  };
  // (переехало в URL)
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // ─── ФИЧА D: Загрузка тегов ───
  useEffect(() => {
    getAllTags()
      .then((data: any) => setAvailableTags(data.tags || []))
      .catch(() => setAvailableTags([]));
  }, []);
  // (переехало в URL)
  // (переехало в URL)
  // ★ Map: testId → resultId (для сертификата)
  const [passedResults, setPassedResults] = useState<Map<string, string>>(new Map());
  const passedTestIds = new Set(passedResults.keys());

  // ─── Загружаем пройденные тесты пользователя ───
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/elearning/results?limit=1000');
        const items = Array.isArray(res.data) ? res.data : (res.data.items || []);
        const map = new Map<string, string>();
        items
          .filter((r: any) => r.passed)
          .forEach((r: any) => {
            const tid = String(r.test?._id || r.test);
            const rid = String(r._id || r.id);
            if (tid && rid && !map.has(tid)) map.set(tid, rid);
          });
        setPassedResults(map);
      } catch (e) {
        console.error('Failed to load passed tests:', e);
      }
    })();
  }, []);
  const handleDownloadCert = async (resultId: string) => {
    try {
      const token = localStorage.getItem('token')
                 || localStorage.getItem('accessToken')
                 || localStorage.getItem('authToken');
      const res = await fetch(`/api/elearning/results/${resultId}/certificate`, {
        headers: token ? { Authorization: 'Bearer ' + token } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast('Ошибка: ' + (err.error || res.status), 'error');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${resultId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Сертификат скачан', 'success');
    } catch (e: any) {
      showToast('Ошибка: ' + e.message, 'error');
    }
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  // НОВОЕ: состояние для модального окна
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const data = await getTests({ active: true });
      setTests(data);
      setError(null);
    } catch (err: any) {
      console.error('Ошибка загрузки тестов:', err);
      setError(err.response?.data?.error || 'Не удалось загрузить тесты');
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const favs = await getFavoriteTests();
      setFavorites(new Set(favs.map((t: Test) => t._id)));
    } catch (err) {
      console.error('Ошибка загрузки избранного:', err);
    }
  };

  useEffect(() => {
    fetchTests();
    fetchFavorites();
  }, []);

  const handleDelete = (id: string, title: string) => {
    confirm.showConfirm({
      title: 'Удаление теста',
      message: `Переместить тест "${title}" в корзину?`,
      confirmText: 'Удалить',
      onConfirm: async () => {
        try {
          await softDeleteTest(id);
          showToast('Тест перемещён в корзину', 'success');
          fetchTests();
        } catch (err) {
          showToast('Не удалось удалить тест', 'error');
        }
      },
    });
  };

  const handleToggleFavorite = async (e: React.MouseEvent, testId: string) => {
    e.stopPropagation();
    try {
      const res = await toggleFavorite(testId);
      const newFavorites = new Set(favorites);
      if (res.isFavorite) {
        newFavorites.add(testId);
        showToast('Добавлено в избранное', 'success');
      } else {
        newFavorites.delete(testId);
        showToast('Удалено из избранного', 'success');
      }
      setFavorites(newFavorites);
    } catch (err) {
      showToast('Не удалось изменить избранное', 'error');
    }
  };

  // НОВОЕ: обработчик открытия модального окна отправки
  const handleSendTest = (e: React.MouseEvent, test: Test) => {
    e.stopPropagation();
    setSelectedTest(test);
    setSendModalOpen(true);
  };

  // НОВОЕ: обработчик назначения теста
  const handleAssignTest = (e: React.MouseEvent, test: Test) => {
    e.stopPropagation();
    setSelectedTest(test);
    setAssignModalOpen(true);
  };

  const renderRating = (rating: number = 0) => {
    return (
      <div style={{ display: 'flex', gap: 2, marginTop: 8 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <Star key={i} size={14} fill={i <= Math.round(rating) ? '#faad14' : 'none'} color="#faad14" />
        ))}
        <span style={{ marginLeft: 8, fontSize: 13, color: '#888' }}>({rating.toFixed(1)})</span>
      </div>
    );
  };

  const filteredTests = useMemo(() => {
    let filtered = tests.filter(t => {
      const matchesSearch = t.title.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = category === 'all' || t.category === category;
      const matchesTag = selectedTag === 'all' || (t.tags && t.tags.includes(selectedTag));
      const isPassed = passedTestIds.has(String(t._id));
      const matchesStatus =
        statusFilter === 'all' ? true
        : statusFilter === 'passed' ? isPassed
        : !isPassed;
      return matchesSearch && matchesCategory && matchesTag && matchesStatus;
    });

    // Сортировка
    filtered.sort((a, b) => {
      if (sortBy === 'title') return a.title.localeCompare(b.title, 'ru');
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      if (sortBy === 'time') return (a.timeLimit || 0) - (b.timeLimit || 0);
      // date — по ObjectId (содержит timestamp в первых 4 байтах)
      return String(b._id).localeCompare(String(a._id));
    });

    return filtered;
  }, [tests, search, category, statusFilter, sortBy, passedTestIds]);

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <AlertCircle size={48} color="#ff4d4f" />
          <h2>{error}</h2>
          <button onClick={fetchTests} style={styles.retryButton}>Повторить</button>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={styles.header}>
        <h1 style={styles.title}>Каталог тестов</h1>
        <div style={styles.headerActions}>
          <button onClick={() => navigate('/app/elearning/favorites')} style={styles.favoritesButton}>
            <Heart size={16} /> Избранное
          </button>
          <button onClick={() => navigate('/app/elearning/trash')} style={styles.trashButton}>
            <Archive size={16} /> Корзина
          </button>
          <button onClick={() => navigate('/app/elearning/constructor')} style={styles.createButton}>
            <Plus size={16} /> Новый тест
          </button>
        </div>
      </div>

      <div style={styles.filters}>
        <div style={styles.searchWrapper}>
          <Search size={18} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Поиск тестов..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <button
          onClick={handleExportTests}
          disabled={exportingTests}
          style={{
            padding: '10px 16px',
            background: exportingTests ? '#95a5a6' : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            fontWeight: 700,
            fontSize: 13,
            cursor: exportingTests ? 'wait' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 6px 16px rgba(17, 153, 142, 0.3)',
            whiteSpace: 'nowrap',
          }}
          title="Скачать все тесты с вопросами в Excel"
        >
          📊 {exportingTests ? '...' : 'Экспорт'}
        </button>
        <select value={category} onChange={e => setCategory(e.target.value)} style={styles.filterSelect}>
          <option value="all">Все категории</option>
          <option value="Мотивация">Мотивация</option>
          <option value="Лидерство">Лидерство</option>
          <option value="Комфорт">Комфорт</option>
          <option value="Лояльность">Лояльность</option>
          <option value="Общие">Общие</option>
        </select>
        {/* TAGFIX: всегда показывать селект тегов */}
        <select
          value={selectedTag}
          onChange={e => setSelectedTag(e.target.value)}
          style={styles.filterSelect}
          title="Фильтр по тегу"
          disabled={availableTags.length === 0}
        >
          <option value="all">🏷️ Все теги</option>
          {availableTags.map(tag => (
            <option key={tag} value={tag}>{tag}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as 'all' | 'passed' | 'new')}
          style={styles.filterSelect}
          title="Фильтр по статусу прохождения"
        >
          <option value="all">Все статусы</option>
          <option value="passed">✅ Пройденные</option>
          <option value="new">🆕 Не пройденные</option>
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value as 'date' | 'rating' | 'title' | 'time')}
          style={styles.filterSelect}
          title="Сортировка"
        >
          <option value="date">↓ Сначала новые</option>
          <option value="rating">↓ По рейтингу</option>
          <option value="title">A→Я По названию</option>
          <option value="time">↑ По времени</option>
        </select>
        {/* Кнопка сброса — видима при активных фильтрах */}
        {(search || category !== 'all' || selectedTag !== 'all' || statusFilter !== 'all' || sortBy !== 'date') && (
          <button
            onClick={resetFilters}
            style={{
              padding: '10px 16px',
              background: 'rgba(239,68,68,0.1)',
              color: '#ef4444',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            title="Сбросить все фильтры"
          >
            ✕ Сбросить
          </button>
        )}
      </div>

      {filteredTests.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999' }}>Нет тестов, соответствующих фильтру</p>
      ) : (
        <div style={styles.grid}>
          {filteredTests.map(test => (
            <div key={test._id} style={styles.card}>
              <div onClick={() => navigate(`/app/elearning/tests/${test._id}`)} style={{ cursor: 'pointer' }}>
                <div style={{ position: 'relative' }}>
                  <img src={test.image || '/images/default-test.jpg'} alt={test.title} style={styles.cardImage} />
                  <button
                    onClick={(e) => handleToggleFavorite(e, test._id)}
                    style={styles.favoriteButton}
                    title={favorites.has(test._id) ? 'Удалить из избранного' : 'Добавить в избранное'}
                  >
                    <Heart
                      size={18}
                      fill={favorites.has(test._id) ? '#ff4d4f' : 'none'}
                      color={favorites.has(test._id) ? '#ff4d4f' : '#666'}
                    />
                  </button>
                  {/* НОВОЕ: Кнопка отправки теста */}
                  <button
                    onClick={(e) => handleSendTest(e, test)}
                    style={styles.sendButton}
                    title="Отправить тест"
                  >
                    <Send size={16} color="#2575fc" />
                  </button>
                  {/* Кнопка назначения теста — только для HR-ролей */}
                  {canAssign && (
                    <button
                      onClick={(e) => handleAssignTest(e, test)}
                      style={{ ...styles.sendButton, top: 8, right: 88 }}
                      title="Назначить сотруднику с дедлайном"
                    >
                      <UserPlus size={16} color="#10b981" />
                    </button>
                  )}
                </div>
                <div style={styles.cardContent}>
                  <h3 style={styles.cardTitle}>{test.title}</h3>
                  <p style={styles.cardDesc}>{test.description}</p>
                  <div style={styles.cardMeta}>
                    <span><Clock size={14} /> {test.timeLimit} мин</span>
                    <span><Award size={14} /> {test.points} баллов</span>
                    <span><BookOpen size={14} /> {test.questionsCount || '?'} вопр.</span>
                  </div>
                  {renderRating(test.rating || 0)}
                  <span style={styles.categoryBadge}>{test.category}</span>
                  {passedTestIds.has(String(test._id)) && (
                    <>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        marginLeft: 8,
                        padding: '3px 10px',
                        borderRadius: 12,
                        background: 'rgba(16,185,129,0.15)',
                        color: '#10b981',
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        <BadgeCheck size={14} /> Пройден
                      </span>
                      {passedResults.get(String(test._id)) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const rid = passedResults.get(String(test._id));
                            if (rid) handleDownloadCert(rid);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            marginLeft: 8,
                            padding: '3px 12px',
                            borderRadius: 12,
                            background: 'rgba(102,126,234,0.12)',
                            color: '#667eea',
                            border: '1px solid rgba(102,126,234,0.25)',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                          title="Скачать сертификат"
                        >
                          🎓 Сертификат
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div style={styles.cardActions}>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/app/elearning/constructor/${test._id}`); }}
                  style={styles.actionButton}
                  title="Редактировать"
                >
                  <Edit size={16} color="#2575fc" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(test._id, test.title); }}
                  style={styles.actionButton}
                  title="Удалить"
                >
                  <Trash2 size={16} color="#ff4d4f" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* НОВОЕ: Модальное окно отправки теста */}
      <SendTestModal
        isOpen={sendModalOpen}
        test={selectedTest}
        onClose={() => setSendModalOpen(false)}
      />

      {canAssign && (
        <AssignTestModal
          isOpen={assignModalOpen}
          test={selectedTest}
          onClose={() => setAssignModalOpen(false)}
        />
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  headerActions: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  favoritesButton: {
    background: 'rgba(255,255,255,0.5)',
    border: '1px solid #ff4d4f',
    color: '#ff4d4f',
    borderRadius: 30,
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 500,
  },
  trashButton: {
    background: 'rgba(255,255,255,0.5)',
    border: '1px solid #2575fc',
    color: '#2575fc',
    borderRadius: 30,
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 500,
  },
  createButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    borderRadius: 30,
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 500,
  },
  filters: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    marginBottom: 30,
    flexWrap: 'wrap',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    minWidth: 220,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '12px 20px 12px 40px',
    borderRadius: 30,
    border: '1px solid rgba(255,255,255,0.3)',
    background: 'rgba(255,255,255,0.7)',
    fontSize: 16,
    outline: 'none',
    boxSizing: 'border-box',
  },
  filterSelect: {
    padding: '12px 20px',
    borderRadius: 30,
    border: '1px solid rgba(255,255,255,0.3)',
    background: 'rgba(255,255,255,0.7)',
    fontSize: 16,
    outline: 'none',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 24,
  },
  card: {
    background: 'rgba(255,255,255,0.5)',
    borderRadius: 20,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.3)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s',
    position: 'relative',
  },
  cardImage: { width: '100%', height: 160, objectFit: 'cover' },
  favoriteButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    background: 'rgba(255,255,255,0.95)',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(0,0,0,0.08)',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    transition: 'transform 0.1s ease',
  },
  // НОВОЕ: стиль кнопки отправки
  sendButton: {
    position: 'absolute',
    top: 8,
    right: 48,
    zIndex: 10,
    background: 'rgba(255,255,255,0.95)',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(0,0,0,0.08)',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    transition: 'transform 0.1s ease',
  },
  cardContent: { padding: 20, paddingBottom: 60 },
  cardTitle: { fontSize: 18, marginBottom: 8, color: '#1a1a2e' },
  cardDesc: { fontSize: 14, color: '#666', marginBottom: 16 },
  cardMeta: {
    display: 'flex',
    gap: 16,
    fontSize: 13,
    color: '#888',
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  categoryBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    background: 'rgba(37,117,252,0.1)',
    borderRadius: 20,
    fontSize: 12,
    color: '#2575fc',
    marginTop: 8,
  },
  cardActions: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    display: 'flex',
    gap: 8,
  },
  actionButton: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  retryButton: {
    marginTop: 20,
    padding: '10px 24px',
    background: '#2575fc',
    color: 'white',
    border: 'none',
    borderRadius: 30,
    cursor: 'pointer',
    fontWeight: 600,
  },
};

export default TestsList;